/**
 * sigilsPipeline — sacred geometry: overlapping circles forming
 * a flower-of-life pattern, with each circle being a tiny rotating
 * mandala.
 *
 * The "Flower of Life" is a sacred geometry pattern of 19
 * overlapping circles. We render this as a 2D pattern where each
 * circle is not just an outline but a small rotating mandala —
 * so the pattern has life in it. The whole pattern slowly rotates
 * and breathes.
 *
 * Innovations:
 *   - Each circle is rendered as a soft glowing ring, not a hard
 *     line. The ring has a thickness that varies around its
 *     circumference (so it looks like a hand-drawn mandala).
 *   - Inside each circle, a smaller mandala rotates at a different
 *     speed, creating a "galaxy within a flower" effect.
 *   - The full pattern is composed of 19 circles arranged in the
 *     classic Flower of Life pattern.
 *   - A central "seed" circle glows brighter than the others.
 *   - Audio: bass pulses the pattern, mid drives the mandala
 *     rotation, high drives the color shift.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface SigilsPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

// Position of circle i in the Flower of Life pattern.
// Index 0 is the central circle, indices 1-6 are the first ring,
// indices 7-18 are the outer rings.
vec2 circlePos(int i) {
  if (i == 0) return vec2(0.0);
  if (i <= 6) {
    float ang = float(i - 1) * 1.0472;  // 60 degrees
    return vec2(cos(ang), sin(ang)) * 0.10;
  }
  // Outer ring: 12 more circles in two rings.
  float fi = float(i - 7);
  float ring = floor(fi / 6.0);
  float ang = (mod(fi, 6.0) + ring * 0.5) * 1.0472;
  float r = 0.20 + ring * 0.10;
  return vec2(cos(ang), sin(ang)) * r;
}

// Render a single circle as a soft glowing ring with internal mandala.
vec3 renderCircle(vec2 p, vec2 center, float radius, float time, float seed, float bass, float mid, float high) {
  vec2 d = p - center;
  float r = length(d);
  float ang = atan(d.y, d.x);
  // Distance to the ring boundary.
  float ringD = abs(r - radius);
  // Soft ring with thickness varying around circumference.
  float thickness = 0.006 + 0.003 * sin(ang * 6.0 + time * 2.0 + seed);
  float ringMask = 1.0 - smoothstep(0.0, thickness, ringD);
  // Color: gradient around the ring.
  vec3 colA = vec3(0.95, 0.65, 0.30);
  vec3 colB = vec3(0.30, 0.65, 0.95);
  vec3 col = mix(colA, colB, 0.5 + 0.5 * sin(ang + time * 0.5 + seed));
  // Internal mandala — small rotating pattern.
  float mandala = 0.0;
  for (int k = 0; k < 6; k++) {
    float fk = float(k);
    float mandR = 0.02 + 0.015 * sin(time + fk + seed);
    float mandAng = fk * 1.0472 + time * (1.0 + mid * 0.5);
    vec2 mandPos = vec2(cos(mandAng), sin(mandAng)) * mandR;
    float mandD = length(d - mandPos);
    mandala += exp(-mandD * 200.0) * 0.3;
  }
  return col * (ringMask * (1.0 + 0.5 * high) + mandala);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;

  // Slow rotation of the entire pattern.
  float rot = uTime * (0.1 + bass * 0.1);
  float ca = cos(rot), sa = sin(rot);
  vec2 rp = vec2(ca * p.x - sa * p.y, sa * p.x + ca * p.y);
  // Breathing scale.
  float scale = 1.0 + 0.05 * sin(uTime * 0.5) * (0.5 + 0.5 * bass);

  float r = length(p);
  vec3 bg = mix(
    vec3(0.06, 0.04, 0.10),
    vec3(0.01, 0.01, 0.04),
    smoothstep(0.0, 0.7, r)
  );

  vec3 col = bg;

  // Render all 19 circles.
  for (int i = 0; i < 19; i++) {
    vec2 cpos = circlePos(i) * scale;
    float radius = 0.10;
    vec3 contrib = renderCircle(rp, cpos, radius, uTime, float(i), bass, mid, high);
    // The central circle is brighter.
    if (i == 0) {
      contrib *= 1.5;
      // Central seed: a bright dot.
      float seedD = length(rp);
      contrib += vec3(0.95, 0.85, 0.5) * exp(-seedD * 100.0) * 2.0;
    }
    col += contrib;
  }

  // Outer aura — a large soft glow.
  float aura = exp(-r * 4.0) * (0.3 + 0.4 * bass);
  col += vec3(0.7, 0.5, 0.9) * aura * 0.3;

  // Tiny stars in the background.
  for (int i = 0; i < 15; i++) {
    float fi = float(i);
    vec2 sp = vec2(
      sin(fi * 1.7 + 0.3) * 1.2,
      cos(fi * 2.1 + 1.7) * 1.0
    );
    float d = length(p - sp);
    col += vec3(0.85, 0.9, 1.0) * exp(-d * 200.0) * 0.3;
  }

  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;

  outColor = vec4(col, 1.0);
}
`;

export function computeSigilsBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeSigilsRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createSigilsPipeline(gl: WebGL2RenderingContext): SigilsPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawSigils(
  gl: WebGL2RenderingContext,
  pipe: SigilsPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
